import type {
  UseMutationResult,
  UseQueryResult,
  QueryClient,
} from "react-query";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import config from "../../config";
import { getAsync, putAsync } from "../protect-api";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import { apiVersion } from "../../shared/constants/common.constant";
import type { RootState } from "../../store/store";
import type {
  UserPermissionsFetchResult,
  ApiPermissionsResponse,
} from "./permissions.types";
import { mapApiPermissionsToFetchResult } from "./permissions.types";

export const PERMISSIONS_QUERY_KEY = "user_permissions";
export const PERMISSION_PROFILE_QUERY_KEY = "user_permission_profile";
export const ROLE_PERMISSIONS_KEY = "role_permissions";
export const ROLES_PERMISSIONS_LIST_KEY = "roles_permissions_list";

export interface IUseGetUserPermissionsOptions {
  enabled: boolean;
}

export interface RolePermissionPayloadItem {
  permissionId: number;
  isAllowed: boolean;
}

export interface RolePermission {
  id: number;
  isAllowed: boolean;
  code?: string;
  name?: string;
  category?: string;
}

export interface RoleWithPermissionsListItem {
  id: number;
  name: string;
  permissions: RolePermission[];
  modifiedAt: string | null;
  modifiedBy: string | null;
  modifiedById: number | null;
}

export interface RolesPermissionsListResponse {
  items: RoleWithPermissionsListItem[];
  count: number;
}

export interface RolePermissionsGetResponse {
  roleId: number;
  roleName: string;
  permissions: RolePermission[];
}

export type RolePermissionsQueryData = RolePermission[];

export interface SaveRolePermissionsInput {
  roleId: number;
  permissions: RolePermission[];
}

export interface GetRolePermissionsOptions {
  defaultOnly?: boolean;
  teamId?: string | null;
}

export interface PutRolePermissionsOptions {
  teamId?: string | null;
}

export type RolePermissionsPutResponse = RolePermissionsGetResponse;

export const buildUserPermissionsUrl = (): string =>
  `${config.KYCP_AUTH_API_URL}/${apiVersion}/users/me/roles/permissions`;

export const getUserPermissions =
  async (): Promise<UserPermissionsFetchResult> => {
    const { data } = await getAsync<ApiPermissionsResponse>(
      buildUserPermissionsUrl(),
      undefined,
      undefined,
      true
    );
    return mapApiPermissionsToFetchResult(data);
  };

export const useGetUserPermissions = (
  options: IUseGetUserPermissionsOptions
): UseQueryResult<UserPermissionsFetchResult, ProblemDetails> => {
  const { enabled } = options;
  return useQuery<UserPermissionsFetchResult, ProblemDetails>(
    [PERMISSIONS_QUERY_KEY, enabled],
    () => getUserPermissions(),
    {
      enabled,
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    }
  );
};

export const prefetchUserPermissions = async (
  queryClient: QueryClient,
  options?: { enabled?: boolean }
): Promise<void> => {
  if (options?.enabled === false) {
    return;
  }
  await queryClient.prefetchQuery([PERMISSIONS_QUERY_KEY], getUserPermissions, {
    staleTime: 5 * 60 * 1000,
  });
};

const selectPermissionsTeamId = (state: RootState): string | null =>
  state.permissions.teamId;

export const buildRolePermissionsHeaders = (
  teamId?: string | null
): Record<string, string> | undefined =>
  teamId?.length ? { "X-Team-Id": teamId } : undefined;

export const buildRolesPermissionsListUrl = (): string =>
  `${config.KYCP_AUTH_API_URL}/${apiVersion}/Roles/permissions`;

export const getRolesPermissionsList = async (options?: {
  teamId?: string | null;
}): Promise<RolesPermissionsListResponse> => {
  const headers = buildRolePermissionsHeaders(options?.teamId);
  const { data } = await getAsync<RolesPermissionsListResponse>(
    buildRolesPermissionsListUrl(),
    headers,
    undefined,
    true
  );
  return data;
};

export const useRolesPermissionsList = (): UseQueryResult<
  RolesPermissionsListResponse,
  ProblemDetails
> => {
  const teamId = useSelector(selectPermissionsTeamId);

  return useQuery<RolesPermissionsListResponse, ProblemDetails>(
    [ROLES_PERMISSIONS_LIST_KEY, teamId],
    () => getRolesPermissionsList({ teamId }),
    {
      retry: 1,
      retryDelay: 0,
      refetchOnWindowFocus: false,
    }
  );
};

export const buildRolePermissionsGetUrl = (
  roleId: number,
  options?: GetRolePermissionsOptions
): string => {
  const base = `${config.KYCP_AUTH_API_URL}/${apiVersion}/roles/${roleId}/permissions`;
  return options?.defaultOnly ? `${base}?defaultOnly=true` : base;
};

export const getRolePermissions = async (
  roleId: number,
  options?: GetRolePermissionsOptions
): Promise<RolePermissionsGetResponse> => {
  const headers = buildRolePermissionsHeaders(options?.teamId);
  const { data } = await getAsync<RolePermissionsGetResponse>(
    buildRolePermissionsGetUrl(roleId, options),
    headers,
    undefined,
    true
  );
  return data;
};

export const useGetRolePermissions = (
  roleId: number
): UseQueryResult<RolePermissionsQueryData, ProblemDetails> => {
  const teamId = useSelector(selectPermissionsTeamId);

  return useQuery<RolePermissionsQueryData, ProblemDetails>(
    [ROLE_PERMISSIONS_KEY, roleId, teamId],
    async () => {
      const response = await getRolePermissions(roleId, { teamId });
      return response.permissions;
    },
    {
      enabled: roleId > 0,
      retry: 1,
      retryDelay: 0,
    }
  );
};

export const buildRolePermissionsPutBody = (
  permissions: RolePermission[]
): RolePermissionPayloadItem[] =>
  permissions.map((permission) => ({
    permissionId: permission.id,
    isAllowed: permission.isAllowed,
  }));

export const putRolePermissions = async (
  roleId: number,
  body: RolePermissionPayloadItem[],
  options?: PutRolePermissionsOptions
): Promise<RolePermissionsPutResponse> => {
  const headers = buildRolePermissionsHeaders(options?.teamId);
  const { data } = await putAsync<RolePermissionsPutResponse>(
    `${config.KYCP_AUTH_API_URL}/${apiVersion}/roles/${roleId}/permissions`,
    body,
    headers,
    true
  );
  return data;
};

export const useSaveRolePermissions = (): UseMutationResult<
  RolePermissionsPutResponse,
  ProblemDetails,
  SaveRolePermissionsInput
> => {
  const queryClient = useQueryClient();
  const teamId = useSelector(selectPermissionsTeamId);

  return useMutation<
    RolePermissionsPutResponse,
    ProblemDetails,
    SaveRolePermissionsInput
  >(
    async ({ roleId, permissions }) => {
      const body: RolePermissionPayloadItem[] =
        buildRolePermissionsPutBody(permissions);
      return putRolePermissions(roleId, body, { teamId });
    },
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<RolePermissionsQueryData>(
          [ROLE_PERMISSIONS_KEY, variables.roleId, teamId],
          data.permissions
        );
      },
    }
  );
};
